import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "ms_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore
    }
  }, [items]);

  const addItem = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((p) => p.slug === product.slug);
      if (existing) {
        return prev.map((p) =>
          p.slug === product.slug ? { ...p, quantity: Math.min(99, p.quantity + qty) } : p
        );
      }
      return [
        ...prev,
        {
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.image || product.images?.[0],
          quantity: qty,
        },
      ];
    });
    setDrawerOpen(true);
  }, []);

  const removeItem = useCallback((slug) => {
    setItems((prev) => prev.filter((p) => p.slug !== slug));
  }, []);

  const updateQty = useCallback((slug, quantity) => {
    setItems((prev) =>
      prev
        .map((p) => (p.slug === slug ? { ...p, quantity: Math.max(0, Math.min(99, quantity)) } : p))
        .filter((p) => p.quantity > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { count, subtotal, shipping, total } = useMemo(() => {
    const count = items.reduce((n, p) => n + p.quantity, 0);
    const subtotal = items.reduce((n, p) => n + p.price * p.quantity, 0);
    const shipping = subtotal >= 25 || subtotal === 0 ? 0 : 4.99;
    const total = subtotal + shipping;
    return { count, subtotal, shipping, total };
  }, [items]);

  const value = {
    items, count, subtotal, shipping, total,
    addItem, removeItem, updateQty, clear,
    drawerOpen, setDrawerOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
